import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Surfaces advisory warnings (e.g. a checkbox combination with a cost/data
 * caveat) as a bottom-right `toast.warning` instead of a persistent inline
 * banner. Fires once per render for every warning that newly became active
 * (present now, absent before) — toggling the underlying condition off and
 * back on re-fires it; leaving it active across renders does not re-toast it.
 *
 * Warnings that become active in the same render (e.g. selecting "Demand
 * forecast" also auto-selects "Captured AADT", activating both warnings at
 * once) are combined into a single toast rather than one per warning.
 *
 * `enabled` (default `true`) gates the toast itself, not the tracking: pass
 * the host modal's `open` state so a warning that's already active in the
 * persisted preferences doesn't toast on every mount/page load — only actual
 * changes made while the modal is open do. The seen-set still updates while
 * disabled, so a warning that became active while closed does not fire
 * retroactively the moment the modal opens.
 */
export function useToastOnNewWarnings(warnings: string[], enabled = true) {
  const previousRef = useRef<Set<string>>(new Set());

  // `warnings` is a fresh array each render; join it to a stable string so
  // this only re-runs when its actual content changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: depend on the joined content, not the array identity
  useEffect(() => {
    const newWarnings = warnings.filter((warning) => !previousRef.current.has(warning));
    previousRef.current = new Set(warnings);
    if (!enabled) return;

    if (newWarnings.length === 1) {
      toast.warning(newWarnings[0]);
    } else if (newWarnings.length > 1) {
      toast.warning(
        <ul className="list-disc space-y-0.5 pl-4">
          {newWarnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>,
      );
    }
  }, [warnings.join('\n'), enabled]);
}
